import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { EngagementEventType } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { listEngagements } from "@/api/engagements";
import { useDebouncedValue } from "@/hooks/usePagination";

type TypeFilter = "all" | EngagementEventType;

const TYPE_FILTERS: { id: TypeFilter; label: string; path?: string }[] = [
  { id: "all", label: "All", path: "/conversations" },
  { id: "email", label: "Email", path: "/emails" },
  { id: "call", label: "Call", path: "/calls" },
  { id: "meeting", label: "Meeting" },
  { id: "note", label: "Note" },
  { id: "stage_change", label: "Stage" },
  { id: "signal", label: "Signal" },
];

function typeBadge(type: EngagementEventType): string {
  switch (type) {
    case "email":
      return "bg-sky-500/15 text-sky-400";
    case "call":
      return "bg-emerald-500/15 text-emerald-400";
    case "meeting":
      return "bg-violet-500/15 text-violet-400";
    case "note":
      return "bg-muted/40 text-muted-foreground";
    case "stage_change":
      return "bg-amber-500/15 text-amber-400";
    case "signal":
      return "bg-brand-soft text-brand";
    default:
      return "bg-muted/30 text-muted-foreground";
  }
}

function titleFor(pathname: string, type: TypeFilter): { title: string; blurb: string } {
  if (pathname.startsWith("/emails") || type === "email") {
    return {
      title: "Emails",
      blurb: "Email engagements across your deals.",
    };
  }
  if (pathname.startsWith("/calls") || type === "call") {
    return {
      title: "Calls",
      blurb: "Call activity logged across your deals.",
    };
  }
  return {
    title: "Conversations",
    blurb: "Engagement timeline across all deals — emails, calls, meetings, and notes.",
  };
}

export function ActivityPage({
  initialType = "all",
}: {
  initialType?: TypeFilter;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [type, setType] = useState<TypeFilter>(initialType);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim(), 300);

  useEffect(() => {
    setType(initialType);
  }, [initialType]);

  const { data: engagements = [], isLoading, error } = useQuery({
    queryKey: ["engagements", "all", debouncedQ],
    queryFn: () => listEngagements({ limit: 200, q: debouncedQ || undefined }),
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    const list =
      type === "all" ? engagements : engagements.filter((e) => e.type === type);
    return [...list].sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
    );
  }, [engagements, type]);

  const { title, blurb } = titleFor(location.pathname, type);

  function selectType(next: TypeFilter) {
    setType(next);
    const dest = TYPE_FILTERS.find((t) => t.id === next)?.path;
    if (dest && dest !== location.pathname) navigate(dest);
  }

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
          <p className="text-muted-foreground">{blurb}</p>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search conversations…"
          className="w-56 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
      </header>

      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-border">
        {TYPE_FILTERS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => selectType(tab.id)}
            className={cn(
              "shrink-0 border-b-2 px-4 py-2 text-sm font-medium transition",
              type === tab.id
                ? "border-brand text-brand"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}

      {error && !isLoading && (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load activity.
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No engagement events yet. Log activity from a{" "}
            <Link to="/deals" className="text-brand hover:underline">
              deal
            </Link>{" "}
            or sync email from Integrations.
          </p>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <ul className="flex flex-col gap-2">
          {filtered.map((ev) => (
            <li
              key={ev.id}
              className="flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3"
            >
              <span
                className={cn(
                  "mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                  typeBadge(ev.type),
                )}
              >
                {ev.type.replace("_", " ")}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground">{ev.summary}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <time dateTime={ev.at}>
                    {new Date(ev.at).toLocaleString()}
                  </time>
                  {ev.accountId && (
                    <Link
                      to={`/user/accounts/${ev.accountId}`}
                      className="hover:text-brand"
                    >
                      {ev.accountId}
                    </Link>
                  )}
                  {ev.opportunityId && (
                    <Link
                      to={`/deals/${ev.opportunityId}`}
                      className="text-brand hover:underline"
                    >
                      View deal
                    </Link>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
